import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, 
  Loader2, Users, DollarSign, TrendingUp, History, X, 
  CheckCircle, AlertCircle, Save, RefreshCw,
  Eye, EyeOff, Lock, Crown, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/ui/Modal';
import { adminApi, errorMessage } from '../../lib/api';
import { motion } from 'framer-motion';

import { toast } from 'sonner';

// Color theme options
const colorThemes = {
  gray: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', badge: 'bg-purple-500' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700', badge: 'bg-indigo-900' },
  teal: { bg: 'bg-teal-50', border: 'border-teal-300', text: 'text-teal-700', badge: 'bg-teal-500' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', badge: 'bg-blue-500' },
};

// Same wording as the backend validator (SubscriptionTierUpdate._stripe_price_id).
const PRICE_ID_ERROR = "Stripe price ids start with 'price_' (not the product id 'prod_...')";
const PRICE_ID_FIELDS = ['stripe_monthly_price_id', 'stripe_annual_price_id', 'stripe_quarterly_price_id'];

const priceIdError = (value) => {
  const v = (value || '').trim();
  return v && !v.startsWith('price_') ? PRICE_ID_ERROR : '';
};

// Integer cents -> "1347.00" (no floating point involved).
const centsToDollarInput = (cents) => {
  if (cents === null || cents === undefined || cents === '') return '';
  const n = Math.round(Number(cents));
  if (!Number.isFinite(n)) return '';
  return `${Math.trunc(n / 100)}.${String(Math.abs(n % 100)).padStart(2, '0')}`;
};

// "$1,347.00" -> 134700; '' -> null; anything unparseable -> undefined.
const dollarInputToCents = (text) => {
  const v = (text || '').replace(/[$,\s]/g, '');
  if (v === '') return null;
  const m = /^(\d+)(?:\.(\d{0,2}))?$/.exec(v);
  if (!m) return undefined;
  return parseInt(m[1], 10) * 100 + parseInt((m[2] || '').padEnd(2, '0'), 10);
};

const formatCents = (cents) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const buildPayload = (data) => {
  const { quarterly_price, ...rest } = data;
  return {
    ...rest,
    hub_exclusivity_cap: data.hub_exclusivity_cap || null,
    hub_cap: data.hub_cap === '' || data.hub_cap === undefined ? null : data.hub_cap,
    stripe_monthly_price_id: data.stripe_monthly_price_id || null,
    stripe_annual_price_id: data.stripe_annual_price_id || null,
    stripe_quarterly_price_id: data.stripe_quarterly_price_id?.trim() || null,
    quarterly_price_cents: dollarInputToCents(quarterly_price) ?? null,
    badge_text: data.badge_text || null
  };
};

const SubscriptionTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [stats, setStats] = useState({ total_subscribers: 0, mrr: 0, arr: 0, active_tiers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [editModal, setEditModal] = useState({ open: false, tier: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, tier: null, migrateTo: '' });
  const [historyModal, setHistoryModal] = useState({ open: false, history: [], loading: false });
  
  // Form state
  const [formData, setFormData] = useState({
    tier_key: '',
    name: '',
    description: '',
    monthly_price: 0,
    annual_price: 0,
    deal_limit: 1,
    weight: 0,
    features: [],
    hub_exclusivity_cap: null,
    hub_cap: null,
    stripe_monthly_price_id: '',
    stripe_annual_price_id: '',
    stripe_quarterly_price_id: '',
    quarterly_price: '',
    is_enabled: true,
    is_public: true,
    admin_only: false,
    sort_order: 0,
    badge_text: '',
    color_theme: 'gray'
  });
  const [initialForm, setInitialForm] = useState(null);
  const [newFeature, setNewFeature] = useState('');
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/subscription-tiers`);
      setTiers(response.data.tiers);
      setStats(response.data.stats);
    } catch (err) {
      setError('Failed to load subscription tiers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (tierId, currentStatus) => {
    try {
      setToggling(tierId);
      await adminApi.patch(
        `/admin/subscription-tiers/${tierId}/toggle`,
        {}
      );
      
      setTiers(prev => prev.map(t => 
        t.id === tierId ? { ...t, is_enabled: !currentStatus } : t
      ));
      
      toast.success(`Tier ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Failed to toggle tier:', err);
      toast.error('Failed to toggle tier');
    } finally {
      setToggling(null);
    }
  };

  const openEditModal = (tier = null) => {
    if (tier) {
      const initial = {
        tier_key: tier.tier_key || '',
        name: tier.name || '',
        description: tier.description || '',
        monthly_price: tier.monthly_price || 0,
        annual_price: tier.annual_price || 0,
        deal_limit: tier.deal_limit || 1,
        weight: tier.weight || 0,
        features: tier.features || [],
        hub_exclusivity_cap: tier.hub_exclusivity_cap,
        hub_cap: tier.hub_cap ?? null,
        stripe_monthly_price_id: tier.stripe_monthly_price_id || '',
        stripe_annual_price_id: tier.stripe_annual_price_id || '',
        stripe_quarterly_price_id: tier.stripe_quarterly_price_id || '',
        quarterly_price: centsToDollarInput(tier.quarterly_price_cents),
        is_enabled: tier.is_enabled ?? true,
        is_public: tier.is_public ?? true,
        admin_only: tier.admin_only ?? false,
        sort_order: tier.sort_order || 0,
        badge_text: tier.badge_text || '',
        color_theme: tier.color_theme || 'gray'
      };
      setFormData(initial);
      setInitialForm(initial);
    } else {
      setInitialForm(null);
      setFormData({
        tier_key: '',
        name: '',
        description: '',
        monthly_price: 0,
        annual_price: 0,
        deal_limit: 1,
        weight: 0,
        features: [],
        hub_exclusivity_cap: null,
        stripe_monthly_price_id: '',
        stripe_annual_price_id: '',
        stripe_quarterly_price_id: '',
        quarterly_price: '',
        is_enabled: true,
        is_public: true,
        admin_only: false,
        sort_order: tiers.length,
        badge_text: '',
        color_theme: 'gray'
      });
    }
    setEditModal({ open: true, tier });
  };

  const quarterlyPriceError =
    dollarInputToCents(formData.quarterly_price) === undefined
      ? 'Enter a dollar amount, e.g. 1,347.00'
      : '';
  const fieldErrors = {
    ...Object.fromEntries(PRICE_ID_FIELDS.map((f) => [f, priceIdError(formData[f])])),
    quarterly_price: quarterlyPriceError,
  };

  const handleSave = async () => {
    const firstError = Object.values(fieldErrors).find(Boolean);
    if (firstError) {
      toast.error(firstError);
      return;
    }
    try {
      setSaving(true);
      
      const payload = buildPayload(formData);
      
      if (editModal.tier) {
        // Update existing: send only the fields that changed.
        const before = initialForm ? buildPayload(initialForm) : {};
        const changed = Object.fromEntries(
          Object.entries(payload).filter(
            ([key, value]) => JSON.stringify(value) !== JSON.stringify(before[key])
          )
        );
        if (Object.keys(changed).length === 0) {
          toast.info('No changes to save');
          setEditModal({ open: false, tier: null });
          return;
        }
        await adminApi.put(
          `/admin/subscription-tiers/${editModal.tier.id}`,
          changed
        );
        toast.success('Tier updated successfully');
      } else {
        // Create new
        await adminApi.post(
          `/admin/subscription-tiers`,
          payload
        );
        toast.success('Tier created successfully');
      }
      
      setEditModal({ open: false, tier: null });
      await fetchTiers();
    } catch (err) {
      console.error('Failed to save tier:', err);
      toast.error(errorMessage(err, 'Failed to save tier'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.migrateTo) {
      toast.error('Please select a tier to migrate subscribers to');
      return;
    }
    
    try {
      setSaving(true);
      await adminApi.post(
        `/admin/subscription-tiers/${deleteModal.tier.id}/delete`,
        { migrate_to_tier_id: deleteModal.migrateTo }
      );
      
      toast.success('Tier deleted and subscribers migrated');
      setDeleteModal({ open: false, tier: null, migrateTo: '' });
      await fetchTiers();
    } catch (err) {
      console.error('Failed to delete tier:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete tier');
    } finally {
      setSaving(false);
    }
  };

  const fetchHistory = async (tierId = null) => {
    try {
      setHistoryModal({ open: true, history: [], loading: true });
      const url = tierId 
        ? `/admin/subscription-tiers/${tierId}/history`
        : `/admin/subscription-tiers/history/all`;
      
      const response = await adminApi.get(url);
      
      setHistoryModal({ open: true, history: response.data.history, loading: false });
    } catch (err) {
      console.error('Failed to fetch history:', err);
      toast.error('Failed to load history');
      setHistoryModal({ open: false, history: [], loading: false });
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTheme = (colorTheme) => colorThemes[colorTheme] || colorThemes.gray;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-business border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* V5 Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-business text-sm mb-1">// settings.subscriptions</p>
            <h1 className="font-display text-3xl text-navy">Subscription Tiers</h1>
            <p className="text-navy/50 mt-1">Manage pricing plans and features</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => fetchHistory()}
              className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              View History
            </button>
            <button
              onClick={fetchTiers}
              className="px-4 py-2.5 border border-navy/20 rounded-xl text-navy/70 hover:bg-white hover:border-navy/30 transition-all flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => openEditModal()}
              className="px-5 py-2.5 bg-gradient-to-r from-business to-purple-400 rounded-xl text-white font-medium hover:shadow-lg hover:shadow-business/20 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Tier
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* V5 Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="stat-card purple bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
            <div className="w-12 h-12 bg-business/10 rounded-xl flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-business" />
            </div>
            <p className="text-4xl font-bold text-navy">{stats.total_subscribers}</p>
            <p className="text-navy/50 text-sm">Total Subscribers</p>
          </div>
          <div className="stat-card green bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-4xl font-bold text-navy">{formatCurrency(stats.mrr)}</p>
            <p className="text-navy/50 text-sm">Monthly Revenue</p>
          </div>
          <div className="stat-card indigo bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-indigo-600" />
            </div>
            <p className="text-4xl font-bold text-navy">{formatCurrency(stats.arr)}</p>
            <p className="text-navy/50 text-sm">Annual Revenue</p>
          </div>
          <div className="stat-card orange bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
            <div className="w-12 h-12 bg-consumer/10 rounded-xl flex items-center justify-center mb-4">
              <CreditCard className="w-6 h-6 text-consumer" />
            </div>
            <p className="text-4xl font-bold text-navy">{stats.active_tiers}</p>
            <p className="text-navy/50 text-sm">Active Tiers</p>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="space-y-4">
          {tiers.map((tier) => {
            const theme = getTheme(tier.color_theme);
            const isDisabled = !tier.is_enabled;
            
            return (
              <motion.div
                key={tier.id}
                className={`bg-white rounded-xl border-2 ${isDisabled ? 'border-gray-200 opacity-60' : theme.border} overflow-hidden`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className={`px-6 py-4 ${theme.bg}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${theme.badge}`}></div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={`text-xl font-bold ${theme.text}`}>{tier.name}</h3>
                          {tier.admin_only && (
                            <span className="px-2 py-0.5 bg-gray-800 text-white text-xs font-medium rounded-full flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              Admin Only
                            </span>
                          )}
                          {!tier.is_public && !tier.admin_only && (
                            <span className="px-2 py-0.5 bg-gray-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                              <EyeOff className="w-3 h-3" />
                              Hidden
                            </span>
                          )}
                          {tier.badge_text && (
                            <span className={`px-2 py-0.5 ${theme.badge} text-white text-xs font-medium rounded-full flex items-center gap-1`}>
                              <Star className="w-3 h-3" />
                              {tier.badge_text}
                            </span>
                          )}
                          {tier.is_enabled ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                              Disabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{tier.description}</p>
                      </div>
                    </div>
                    
                    {/* Subscriber Count */}
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{tier.subscriber_count}</p>
                        <p className="text-sm text-gray-500">subscribers</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(tier)}
                          className="flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Button>
                        {tier.tier_key !== 'free' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteModal({ open: true, tier, migrateTo: '' })}
                            className="flex items-center gap-1 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <button
                          onClick={() => handleToggle(tier.id, tier.is_enabled)}
                          disabled={toggling === tier.id}
                          className={`p-2 rounded-lg transition-all ${
                            tier.is_enabled
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          {toggling === tier.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : tier.is_enabled ? (
                            <ToggleRight className="w-5 h-5" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Tier Details */}
                <div className="px-6 py-4 border-t border-gray-100">
                  <div className={`grid grid-cols-2 ${tier.quarterly_price_cents != null ? 'md:grid-cols-6' : 'md:grid-cols-5'} gap-4 text-sm`}>
                    <div>
                      <p className="text-gray-500">Monthly Price</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(tier.monthly_price)}/mo</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Annual Price</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(tier.annual_price)}/yr</p>
                    </div>
                    {tier.quarterly_price_cents != null && (
                      <div>
                        <p className="text-gray-500">Quarterly Price</p>
                        <p className="font-semibold text-gray-900">{formatCents(tier.quarterly_price_cents)}/qtr</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500">Deal Limit</p>
                      <p className="font-semibold text-gray-900">{tier.deal_limit} deals</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Weight (legacy)</p>
                      <p className="font-semibold text-gray-900">{tier.weight}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Venue cap</p>
                      <p className="font-semibold text-gray-900">
                        {tier.hub_cap ? `${tier.hub_cap} venue${tier.hub_cap !== 1 ? 's' : ''}` : 'Unlimited'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Features */}
                  {tier.features && tier.features.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-gray-500 mb-2">Features:</p>
                      <div className="flex flex-wrap gap-2">
                        {tier.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {tiers.length === 0 && !loading && (
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">No subscription tiers configured</h3>
            <p className="text-gray-500 mt-2">Click &quot;Add Tier&quot; to create your first tier</p>
          </div>
        )}
      </div>

      {/* Edit/Create Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, tier: null })}
        maxWidth="max-w-2xl"
        headingClassName="text-lg font-bold text-gray-900"
        title={
          <span className="flex items-center gap-2">
            {editModal.tier ? <Edit2 className="w-5 h-5 text-purple-600" aria-hidden="true" /> : <Plus className="w-5 h-5 text-purple-600" aria-hidden="true" />}
            {editModal.tier ? 'Edit Tier' : 'Create New Tier'}
          </span>
        }
      >
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tier Key *</label>
                    <input
                      type="text"
                      value={formData.tier_key}
                      onChange={(e) => setFormData(prev => ({ ...prev, tier_key: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                      placeholder="e.g., starter"
                      disabled={!!editModal.tier}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Starter"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this tier"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                {/* Pricing */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Pricing</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($)</label>
                      <input
                        type="number"
                        value={formData.monthly_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, monthly_price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Price ($)</label>
                      <input
                        type="number"
                        value={formData.annual_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, annual_price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="tier-quarterly-price" className="block text-sm font-medium text-gray-700 mb-1">Quarterly price ($)</label>
                      <input
                        id="tier-quarterly-price"
                        type="text"
                        inputMode="decimal"
                        value={formData.quarterly_price}
                        onChange={(e) => setFormData(prev => ({ ...prev, quarterly_price: e.target.value }))}
                        placeholder="e.g. 1,347.00"
                        aria-invalid={!!fieldErrors.quarterly_price}
                        aria-describedby={fieldErrors.quarterly_price ? 'tier-quarterly-price-error' : 'tier-quarterly-help'}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 ${fieldErrors.quarterly_price ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {fieldErrors.quarterly_price && (
                        <p id="tier-quarterly-price-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors.quarterly_price}</p>
                      )}
                    </div>
                  </div>
                  <p id="tier-quarterly-help" className="text-xs text-gray-500 mt-2">Used by slot-model checkout (Standard, Premium, Sponsor)</p>
                  {editModal.tier?.slot_kind && (
                    <dl className="grid grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg text-sm" aria-label="Slot capacity (from the plan, read-only)">
                      <div>
                        <dt className="text-gray-500">Slots per venue</dt>
                        <dd className="font-semibold text-gray-900">{editModal.tier.slots_per_venue ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Coasters per venue a month</dt>
                        <dd className="font-semibold text-gray-900">{editModal.tier.coasters_per_venue ?? '—'}</dd>
                      </div>
                    </dl>
                  )}
                </div>
                
                {/* Stripe IDs */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Stripe Configuration</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="tier-stripe-monthly" className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ID</label>
                      <input
                        id="tier-stripe-monthly"
                        type="text"
                        value={formData.stripe_monthly_price_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, stripe_monthly_price_id: e.target.value }))}
                        placeholder="price_xxx"
                        aria-invalid={!!fieldErrors.stripe_monthly_price_id}
                        aria-describedby={fieldErrors.stripe_monthly_price_id ? 'tier-stripe-monthly-error' : undefined}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm ${fieldErrors.stripe_monthly_price_id ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {fieldErrors.stripe_monthly_price_id && (
                        <p id="tier-stripe-monthly-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors.stripe_monthly_price_id}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="tier-stripe-annual" className="block text-sm font-medium text-gray-700 mb-1">Annual Price ID</label>
                      <input
                        id="tier-stripe-annual"
                        type="text"
                        value={formData.stripe_annual_price_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, stripe_annual_price_id: e.target.value }))}
                        placeholder="price_yyy"
                        aria-invalid={!!fieldErrors.stripe_annual_price_id}
                        aria-describedby={fieldErrors.stripe_annual_price_id ? 'tier-stripe-annual-error' : undefined}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm ${fieldErrors.stripe_annual_price_id ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {fieldErrors.stripe_annual_price_id && (
                        <p id="tier-stripe-annual-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors.stripe_annual_price_id}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="tier-stripe-quarterly" className="block text-sm font-medium text-gray-700 mb-1">Stripe quarterly price id</label>
                      <input
                        id="tier-stripe-quarterly"
                        type="text"
                        value={formData.stripe_quarterly_price_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, stripe_quarterly_price_id: e.target.value }))}
                        placeholder="price_zzz"
                        aria-invalid={!!fieldErrors.stripe_quarterly_price_id}
                        aria-describedby={fieldErrors.stripe_quarterly_price_id ? 'tier-stripe-quarterly-error' : 'tier-stripe-quarterly-help'}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm ${fieldErrors.stripe_quarterly_price_id ? 'border-red-400' : 'border-gray-200'}`}
                      />
                      {fieldErrors.stripe_quarterly_price_id && (
                        <p id="tier-stripe-quarterly-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors.stripe_quarterly_price_id}</p>
                      )}
                      <p id="tier-stripe-quarterly-help" className="text-xs text-gray-500 mt-1">Used by slot-model checkout (Standard, Premium, Sponsor)</p>
                    </div>
                  </div>
                </div>
                
                {/* Limits */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Limits & Priority</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deal Limit</label>
                      <input
                        type="number"
                        value={formData.deal_limit}
                        onChange={(e) => setFormData(prev => ({ ...prev, deal_limit: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Weight (legacy — not used in Phase 1)</label>
                      <input
                        type="number"
                        value={formData.weight}
                        onChange={(e) => setFormData(prev => ({ ...prev, weight: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Venue cap (max hubs)</label>
                      <input
                        type="number"
                        min="1"
                        value={formData.hub_cap ?? ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, hub_cap: e.target.value ? parseInt(e.target.value) : null }))}
                        placeholder="Empty = unlimited"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Local = 1, Multi = 3. Enforced when a business picks venues.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Hub Exclusivity Cap (legacy, unused)</label>
                      <input
                        type="number"
                        value={formData.hub_exclusivity_cap || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, hub_exclusivity_cap: e.target.value ? parseInt(e.target.value) : null }))}
                        placeholder="Empty = unlimited"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Features</h4>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      placeholder="Add a feature..."
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <Button onClick={addFeature} variant="outline">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-2"
                      >
                        {feature}
                        <button onClick={() => removeFeature(idx)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Appearance */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Appearance</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                      <input
                        type="text"
                        value={formData.badge_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, badge_text: e.target.value }))}
                        placeholder="e.g., Best Value"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Color Theme</label>
                      <select
                        value={formData.color_theme}
                        onChange={(e) => setFormData(prev => ({ ...prev, color_theme: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        {Object.keys(colorThemes).map(theme => (
                          <option key={theme} value={theme}>{theme.charAt(0).toUpperCase() + theme.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* Settings */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Settings</h4>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_enabled}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Enabled (tier is active)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_public}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_public: e.target.checked }))}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Public (visible on pricing page)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.admin_only}
                        onChange={(e) => setFormData(prev => ({ ...prev, admin_only: e.target.checked }))}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Admin Only (only admin can assign)</span>
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setEditModal({ open: false, tier: null })}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving || !formData.tier_key || !formData.name}
                  className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? 'Saving...' : 'Save Tier'}
                </Button>
              </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, tier: null, migrateTo: '' })}
        maxWidth="max-w-md"
        headingClassName="text-lg font-bold text-gray-900"
        title={
          <span className="flex items-center gap-3">
            <span className="p-3 bg-red-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-600" aria-hidden="true" />
            </span>
            <span>
              Delete &quot;{deleteModal.tier?.name}&quot; Tier
              <span className="block text-sm font-normal text-gray-500">
                {deleteModal.tier?.subscriber_count || 0} subscribers will be migrated
              </span>
            </span>
          </span>
        }
      >
              <div>
                
                <p className="text-gray-600 mb-4">
                  Select a tier to migrate all current subscribers to:
                </p>
                
                <div className="space-y-2 mb-6">
                  {tiers.filter(t => t.id !== deleteModal.tier?.id && !t.deleted_at).map(tier => (
                    <label
                      key={tier.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        deleteModal.migrateTo === tier.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="migrateTo"
                        value={tier.id}
                        checked={deleteModal.migrateTo === tier.id}
                        onChange={(e) => setDeleteModal(prev => ({ ...prev, migrateTo: e.target.value }))}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <p className="font-medium text-gray-900">{tier.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatCurrency(tier.monthly_price)}/mo
                          {tier.admin_only && ' • Admin Only'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteModal({ open: false, tier: null, migrateTo: '' })}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    disabled={saving || !deleteModal.migrateTo}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {saving ? 'Deleting...' : 'Migrate & Delete'}
                  </Button>
                </div>
              </div>
      </Modal>

      {/* History Modal */}
      <Modal
        isOpen={historyModal.open}
        onClose={() => setHistoryModal({ open: false, history: [], loading: false })}
        maxWidth="max-w-2xl"
        headingClassName="text-lg font-bold text-gray-900"
        title={
          <span className="flex items-center gap-2">
            <History className="w-5 h-5 text-purple-600" aria-hidden="true" />
            Change History
          </span>
        }
      >
              <div>
                {historyModal.loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                  </div>
                ) : historyModal.history.length === 0 ? (
                  <p className="text-center text-gray-500 py-12">No changes recorded yet</p>
                ) : (
                  <div className="space-y-3">
                    {historyModal.history.map((item) => (
                      <div key={item.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              item.action === 'create' ? 'bg-green-100 text-green-700' :
                              item.action === 'delete' ? 'bg-red-100 text-red-700' :
                              item.action === 'toggle' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {item.action}
                            </span>
                            <span className="font-medium text-gray-900">{item.tier_key}</span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(item.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          By {item.admin_email}
                        </p>
                        {item.changes && Object.keys(item.changes).length > 0 && (
                          <div className="mt-2 text-xs text-gray-500 font-mono bg-gray-100 p-2 rounded">
                            {JSON.stringify(item.changes, null, 2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
      </Modal>
    </AdminLayout>
  );
};

export default SubscriptionTiers;
